import dayjs from 'dayjs';
import moment from 'moment';
import { PropTypes } from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '@ge/components/button';
import { Icon, Icons } from '@ge/components/icon';
import { MiniLoader } from '@ge/components/loader';
import { Dialog } from '@ge/components/modal';
import { useNotification } from '@ge/components/notification';
import { ScrollingContainer } from '@ge/components/scrolling-container';
import { Severity } from '@ge/components/severity';
import { Textarea } from '@ge/components/textarea/textarea';
import { Text } from '@ge/components/typography';
import NotificationMessage from '@ge/feat-monitor/components/notification-message';
import {
  ReasonList,
  DateTimeFormats,
  NotificationType,
  FormMode,
  TaskStatus,
} from '@ge/models/constants';
import { DateMetaField } from '@ge/shared/components/meta-fields';
import { SelectMetaField } from '@ge/shared/components/meta-fields/select-meta-field';
import { TaskProvider } from '@ge/shared/components/tasks/task-provider';
import { useTaskDelayScope, useEditTask } from '@ge/shared/data-hooks';
import { useLogger } from '@ge/shared/hooks';
import { getUserProfileInfo } from '@ge/shared/services/auth';
import { killEventPropagation } from '@ge/shared/util/general';
import { getDelayDuration } from '@ge/shared/util/time-date';
import { elevations, typography, globalColors } from '@ge/tokens';

const StyledNewTaskDialog = styled(Dialog)`
  &.hide-dialog {
    opacity: 0;
    visibility: hidden;
  }
  svg {
    fill: ${(props) => props.theme.mainNav.iconColor};
  }
`;

const ErrorText = styled(Text).attrs(() => ({
  type: typography.textTypes.body,
  level: 2,
}))`
  color: ${(props) => props.theme.dangerColor} !important;
  display: block;
  margin: 5px 0;
`;

const DialogScrollWrapper = styled.div`
  box-sizing: border-box;
  padding: 20px 25px;
`;

const NewTaskForm = styled.form`
  display: flex;
  flex: 1;
  position: relative;
  width: 567px;
  height: 355px;
  max-height: calc(75vh - 101px);
`;

const NewTaskFooterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
  button {
    text-transform: capitalize;
  }
`;

const FooterButtons = styled.div`
  margin-left: auto;
  button {
    &:not(:last-of-type) {
      margin-right: 5px;
    }
  }
`;

const AddDelayIcon = styled(Icon).attrs((props) => ({
  size: 18,
  icon: Icons.DELAY_ICON,
  viewbox: '0 0 18 18',
  color: props.theme.entityDetails.headerIconColor,
}))`
  margin-right: 2px;
`;

const FormRow = styled.section`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  padding-bottom: 15px;
  margin-bottom: 15px;
  border-bottom: 1px solid #1c252d;
`;

const AddDelayRow = styled(FormRow)`
  > div {
    margin-right: 40px;
    .select__control .select__indicators .select__dropdown-indicator svg {
      fill: ${(props) => props.theme.mainNav.iconColor};
    }
    .select__control {
      border: 1px solid #1c252d;
    }
  }
  .option-w-icon {
    margin-left: 8px;
    text-transform: capitalize;
  }
`;
const DatesRow = styled(FormRow)`
  display: flex;
  flex-direction: column;
  border: none;
  margin-bottom: 0px;
  padding-bottom: 12px;
  justify-content: flex-start;
  position: relative;
  z-index: ${elevations.P2};
  > div {
    .date-picker input {
      width: 180px;
    }
  }
  .delayDatesRow {
    display: flex;
    flex-direction: row;
  }
`;

const DelayDuration = styled.div`
  color: #c5d2de;
  font-size: 10px;
  padding-bottom: 10px;
  margin-top: 10px;
  margin-bottom: 15px;
  span {
    font-size: 10px;
    font-weight: 300;
    line-height: 12px;
    letter-spacing: 0.5px;
    color: #c5d2de;
    margin-top: 5px;
  }
`;

export const StyledDateContainer = styled.div`
  &:first-child {
    margin-right: 50px;
  }
  position: relative;
  &:last-of-type {
    margin-right: 0;
  }
  .label {
    color: ${(props) => props.theme.entityDetails.tasks.details.labelColor};
    margin-bottom: 5px;
  }
  .date-picker {
    display: flex;
    align-items: center;
    input {
      border-radius: 3px;
      color: ${(props) => props.theme.entityDetails.tasks.details.inputColor};
      font-size: 12px;
      width: 110px;
    }
    .value {
      margin-right: 40px;
    }
    .task-dialog-date {
      position: absolute;
      bottom: 7px;
      right: -2px;
    }
  }
`;

const SelectMetaFieldDropdown = styled(SelectMetaField)`
  text-transform: capitalize;
  .select__menu {
    .select__menu-list {
      max-height: 200px;
      &::-webkit-scrollbar {
        width: 4px;
        height: 0px;
      }
      &::-webkit-scrollbar-track {
        background: ${(props) => props.theme.scrollbar.trackBackground};
      }
      &::-webkit-scrollbar-thumb {
        background: ${(props) => props.theme.scrollbar.thumbBackground};
        border-radius: 2.5px;
      }
    }
  }
`;

export const StyledTextarea = styled(Textarea)`
  resize: none !important;
  height: 90px;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    height: 37.16px;
    width: 2px;
    border-radius: 2.5px;
    background-color: #445a6c;
  }
  &::-webkit-scrollbar-track {
    height: 68px;
    width: 6px;
    border-radius: 1px;
    background-color: rgba(12, 15, 18, 0.24);
  }
`;

const EntityIcon = styled(Icon).attrs(({ theme, icon }) => ({
  size: 9,
  color: theme.dialog.taskReschedule.iconColor,
  icon,
}))`
  margin-right: 5px;
  vertical-align: initial;
`;

const EntitySiteIcon = styled(Icon).attrs(({ theme, icon }) => ({
  size: 9,
  color: theme.dialog.taskReschedule.iconColor,
  icon,
}))`
  margin-right: 2px;
  position: relative;
  bottom: 1px;
`;

const TaskDetails = styled(FormRow)`
  display: flex;
  flex-direction: column;
  .entity-info-main {
    width: 270px;
    overflow-wrap: break-word;
  }
  .entity-task-name {
    margin-top: 5px;
    .entity-text {
      font-size: 14px;
      margin-left: 3px;
    }
  }
  .entity-info {
    .entity-text {
      color: ${(props) =>
        props.theme.name === 'Light' ? `${globalColors.grey2}` : `${globalColors.slate5}`};
    }
  }
`;

export const DelayDialog = ({
  onClose,
  onConfirm,
  setAnchorEl,
  methods,
  currentDelay,
  task,
  mode,
  isTaskTable,
}) => {
  const { t } = useTranslation(['tasks', 'general'], { useSuspense: false });
  const { watch } = useFormContext();
  const [taskDelayReason, setTaskDelayReason] = useState('');
  const [taskDelayType, setTaskDelayType] = useState('');
  const userInfo = getUserProfileInfo();
  const logger = useLogger();
  const { notify, dismiss } = useNotification();
  const isTaskCompleted = task.status === TaskStatus.COMPLETED;

  const { isLoading: isTaskDelayLoading, data: taskDelayScopes } = useTaskDelayScope(task.id);

  const { register } = methods;
  const watchDelayReason = watch('taskDelayReason');
  const watchDelayTypes = watch('taskDelayTypes');
  const watchDelayStartDate = watch('delayStartDate');
  const watchDelayEndDate = watch('delayEndDate');
  const taskCompleteDate = useMemo(() => (isTaskCompleted ? task.completedDateTime : null), []);
  const isDelayEligible = useMemo(() => {
    if (
      mode === FormMode.EDIT &&
      dayjs(watchDelayEndDate).add(1, 'day').startOf('date').isBefore(dayjs(watchDelayStartDate))
    ) {
      return true;
    } else if (
      mode === FormMode.CREATE &&
      dayjs(watchDelayEndDate)
        .add(1, 'day')
        .startOf('date')
        .isSameOrBefore(dayjs(watchDelayStartDate))
    ) {
      return true;
    } else {
      return false;
    }
  }, [watchDelayStartDate, mode, watchDelayEndDate]);

  const getDaysCount = useMemo(() => {
    if (watchDelayStartDate && watchDelayEndDate) {
      return getDelayDuration(t, { startDate: watchDelayStartDate, endDate: watchDelayEndDate });
    }
  }, [watchDelayStartDate, watchDelayEndDate]);

  const delayReasonOptions = useMemo(() => {
    const taskReason = [];
    taskDelayScopes?.forEach((c) => {
      taskReason.push({
        value: c.reason,
        label: c.reasonDesc,
      });
    });
    return taskReason;
  }, [taskDelayScopes]);

  const delayReasonTypes = useMemo(() => {
    const taskType = [];
    taskDelayScopes?.filter((c) => {
      if (c.reason === ReasonList.WEATHER && watchDelayReason === ReasonList.WEATHER) {
        c?.types?.filter((types) => {
          taskType.push({
            value: types,
            label: types,
          });
        });
      } else if (
        c.reason === ReasonList.CUSTOMER_RESTRUCTIONS &&
        watchDelayReason === ReasonList.CUSTOMER_RESTRUCTIONS
      ) {
        c?.types?.filter((types) => {
          taskType.push({
            value: types,
            label: types,
          });
        });
      }
    });
    return taskType.sort((a, b) => (a.value > b.value ? 1 : b.value > a.value ? -1 : 0));
  }, [taskDelayScopes, watchDelayReason]);

  const dropDownValues = useMemo(
    () =>
      watchDelayReason === ReasonList.WEATHER ||
      watchDelayReason === ReasonList.CUSTOMER_RESTRUCTIONS,
    [watchDelayReason],
  );

  const delayTypesCheck = useMemo(() => {
    const findReasonType = delayReasonTypes?.some((item) => item.value === watchDelayTypes);
    return findReasonType;
  }, [delayReasonTypes, watchDelayTypes]);

  const disableSaveDelays = useMemo(
    () => !(watchDelayReason && watchDelayStartDate),
    [watchDelayStartDate, watchDelayReason],
  );

  const handleReasonSelect = useCallback(
    (option) => {
      setTaskDelayReason(option);
    },
    [setTaskDelayReason],
  );

  const handleTypeSelect = useCallback(
    (option) => {
      setTaskDelayType(option);
    },
    [setTaskDelayType],
  );

  const delayReasonType = useMemo(() => {
    return (
      <>
        <SelectMetaFieldDropdown
          defaultValue={taskDelayReason}
          label={t('action_buttons.delay_reason', 'Delay Reason')}
          metadata={{ required: true }}
          maxWidth={150}
          minWidth={200}
          name="taskDelayReason"
          options={delayReasonOptions}
          placeholder={t(`action_buttons.default_select`, 'Please Select')}
          handleChange={(option) => handleReasonSelect(option)}
          searchable={false}
        />
        {dropDownValues && (
          <SelectMetaFieldDropdown
            defaultValue={taskDelayType}
            label={t('action_buttons.delay_type', 'Type')}
            metadata={{ required: true }}
            maxWidth={150}
            minWidth={200}
            name="taskDelayTypes"
            options={delayReasonTypes}
            handleChange={(option) => handleTypeSelect(option)}
            placeholder={t(`action_buttons.default_select`, 'Please Select')}
            searchable={false}
          />
        )}
      </>
    );
  }, [
    delayReasonOptions,
    handleTypeSelect,
    delayReasonTypes,
    handleReasonSelect,
    taskDelayReason,
    taskDelayType,
    dropDownValues,
    t,
  ]);

  const { edit: fetchDelay } = useEditTask({
    onSettled: onConfirm,
    onSuccess: () => {
      dismiss();
      let message;
      if (mode === FormMode.CREATE) {
        message = (
          <NotificationMessage
            message={t('action_buttons.delay_success', 'DELAY ADDED SUCCESSFULLY')}
          />
        );
      } else if (mode === FormMode.EDIT) {
        message = (
          <NotificationMessage
            message={t('action_buttons.edit_delay', 'DELAY UPDATED SUCCESSFULLY')}
          />
        );
      }
      notify({ message });
      onConfirm();
    },
    onError: (e) => {
      if (mode === FormMode.EDIT) {
        const message = (
          <NotificationMessage
            message={t('action_buttons.delay_update_failed', 'DELAY UPDATE FAILED')}
          />
        );
        const type = NotificationType.FAILURE;
        notify({ message, type });
        logger.error('Error updating delay', e);
      } else if (mode === FormMode.CREATE) {
        const message = (
          <NotificationMessage message={t('action_buttons.delay_add_failed', 'ADD DELAY FAILED')} />
        );
        const type = NotificationType.FAILURE;
        notify({ message, type });
        logger.error('Error adding delay', e);
      }
    },
  });

  const getHeaderTitle = useMemo(() => {
    if (mode === FormMode.CREATE) {
      return t('action_buttons.delay_create', 'ADD DELAY');
    } else if (mode === FormMode.EDIT) {
      return t('action_buttons.delay_edit', 'EDIT DELAY');
    } else {
      return t('action_buttons.delay_end', 'END DELAY');
    }
  }, [mode, t]);

  const getDelaySlNo = useMemo(() => task?.delayDetails?.length + 1, [task]);

  const prevDelays = useMemo(() => (task?.delayDetails ? task?.delayDetails : []), [task]);

  const onSubmit = useCallback(
    (values) => {
      const id = task?.id;
      const reasonDescription = delayReasonOptions?.find(
        (value) => value?.value === values?.taskDelayReason,
      );

      if (mode === FormMode.CREATE) {
        const delayPayload = {
          properties: {
            delayDetails: [
              ...prevDelays,
              {
                slNo: getDelaySlNo,
                reason: values?.taskDelayReason,
                reasonDesc: reasonDescription?.label,
                type: values?.taskDelayTypes?.toLowerCase(),
                startDate: dayjs(values.delayStartDate).format(DateTimeFormats.EVENT_DATE_TIME),
                endDate: values?.delayEndDate
                  ? dayjs(values?.delayEndDate).format(DateTimeFormats.EVENT_DATE_TIME)
                  : null,
                notes: values?.delayNote?.trim(),
                contributedBy: userInfo.preferred_username,
                contributedByName: userInfo.name,
                updatedOn: dayjs(new Date()).format(DateTimeFormats.EVENT_DATE_TIME),
                updatedBy: userInfo.preferred_username,
                updatedByName: userInfo.name,
                addedOn: dayjs(new Date()).format(DateTimeFormats.EVENT_DATE_TIME),
              },
            ],
          },
        };
        fetchDelay({ id, task: delayPayload, bundledTaskIds: task?.bundledTasks });
      } else if (mode === FormMode.EDIT && currentDelay) {
        let currentObject = task?.delayDetails;
        currentObject[currentDelay?.index] = {
          slNo: currentDelay?.delay?.slNo,
          reason: values?.taskDelayReason,
          reasonDesc: reasonDescription?.label,
          type: values?.taskDelayTypes?.toLowerCase(),
          startDate: dayjs(values?.delayStartDate).format(DateTimeFormats.EVENT_DATE_TIME),
          endDate: values?.delayEndDate
            ? dayjs(values?.delayEndDate).format(DateTimeFormats.EVENT_DATE_TIME)
            : null,
          notes: values?.delayNote?.trim(),
          contributedBy: userInfo.preferred_username,
          contributedByName: userInfo?.name,
          updatedOn: dayjs(new Date()).format(DateTimeFormats.EVENT_DATE_TIME),
          updatedBy: userInfo.preferred_username,
          updatedByName: userInfo?.name,
          addedOn: dayjs(new Date()).format(DateTimeFormats.EVENT_DATE_TIME),
        };
        const editPayload = {
          properties: {
            delayDetails: [...currentObject],
          },
        };
        fetchDelay({ id, task: editPayload, bundledTaskIds: task?.bundledTasks });
      }

      let message;
      if (mode === FormMode.CREATE) {
        message = (
          <NotificationMessage
            message={t('action_buttons.delay_inprogress', 'ADDING DELAY')}
            isProgressLoader={true}
          />
        );
      } else if (mode === FormMode.EDIT) {
        message = (
          <NotificationMessage
            message={t('action_buttons.edit_inprogress', 'UPDATING DELAY')}
            isProgressLoader={true}
          />
        );
      }

      notify({ message });

      onClose();

      if (setAnchorEl) {
        setAnchorEl(null);
      }
    },
    [
      setAnchorEl,
      notify,
      mode,
      getDelaySlNo,
      fetchDelay,
      delayReasonOptions,
      currentDelay,
      task,
      userInfo,
      prevDelays,
      onClose,
      t,
    ],
  );

  const taskDetails = useMemo(
    () => (
      <TaskDetails>
        <div className="entity-info-main">
          {task.site?.name && (
            <span className="entity-text">
              <EntityIcon icon={Icons.SITE} />
              {task.site.name}
            </span>
          )}

          {task.asset?.name && (
            <>
              <span style={{ color: globalColors.grey3 }}>{' / '}</span>
              <span className="entity-text">
                <EntitySiteIcon icon={Icons.TURBINE} />
                {task.asset.name}
              </span>
            </>
          )}
        </div>
        <div className="entity-task-name">
          {task?.priority && <Severity level={task.priority.toLowerCase()} size={11} />}
          <span className="entity-text large">{task.title}</span>
        </div>
      </TaskDetails>
    ),
    [task],
  );

  const getTaskTemplate = useMemo(() => {
    const maxDate = dayjs(taskCompleteDate).format(DateTimeFormats.CREW_TIMING);
    return (
      <ScrollingContainer>
        <DialogScrollWrapper>
          {isTaskTable && taskDetails}
          <AddDelayRow>{delayReasonType}</AddDelayRow>
          <DatesRow>
            <div className="delayDatesRow">
              <StyledDateContainer>
                <div className="label">{t('action_buttons.delay_start_date', 'Start Date*')}</div>
                <DateMetaField
                  metadata={{ required: true }}
                  name="delayStartDate"
                  validators={[{ latestDate: 'latestDate' }]}
                  max={maxDate}
                />
              </StyledDateContainer>
              <StyledDateContainer>
                <div className="label">
                  {t('action_buttons.delay_end_date', 'Actual/Tentative End Date (Optional)')}
                </div>
                <DateMetaField
                  metadata={{ required: false }}
                  name="delayEndDate"
                  validators={[{ latestDate: 'latestDate' }]}
                  min={watchDelayStartDate}
                  max={maxDate}
                />
                <ErrorText>
                  {mode === FormMode.EDIT
                    ? dayjs(watchDelayEndDate)
                        .add(1, 'day')
                        .startOf('date')
                        .isBefore(dayjs(watchDelayStartDate)) && (
                        <ErrorText>
                          {t(
                            'action_buttons.error_end_date',
                            'End date can not be before start date',
                          )}
                        </ErrorText>
                      )
                    : dayjs(watchDelayEndDate)
                        .add(1, 'day')
                        .startOf('date')
                        .isSameOrBefore(dayjs(watchDelayStartDate)) && (
                        <ErrorText>
                          {t(
                            'action_buttons.error_end_date',
                            'End date can not be before start date',
                          )}
                        </ErrorText>
                      )}
                </ErrorText>
              </StyledDateContainer>
            </div>
          </DatesRow>
          <DelayDuration>
            <span>{getDaysCount}</span>
          </DelayDuration>

          <StyledTextarea
            id="task-delay-textarea"
            label={t('action_buttons.notes_title', 'Notes (Optional)')}
            name="delayNote"
            ref={register}
            maxLength={250}
            onKeyDown={(e) => {
              killEventPropagation(e);
            }}
            onChange={(e) => {
              killEventPropagation(e);
            }}
          />
        </DialogScrollWrapper>
      </ScrollingContainer>
    );
  }, [
    delayReasonType,
    getDaysCount,
    register,
    watchDelayEndDate,
    watchDelayStartDate,
    mode,
    isDelayEligible,
    taskCompleteDate,
    t,
  ]);

  // Build the footer to pass into the Dialog component.
  const getFooter = useMemo(() => {
    return (
      <NewTaskFooterWrapper>
        <FooterButtons>
          <Button onClick={onClose}>{t('general:cancel', 'Cancel')}</Button>
          <Button
            primary
            onClick={methods.handleSubmit(onSubmit)}
            disabled={isDelayEligible || disableSaveDelays || (dropDownValues && !delayTypesCheck)}
          >
            {t('general:save', 'Save')}
          </Button>
        </FooterButtons>
      </NewTaskFooterWrapper>
    );
  }, [
    t,
    methods,
    onSubmit,
    onClose,
    disableSaveDelays,
    delayTypesCheck,
    dropDownValues,
    isDelayEligible,
  ]);

  return (
    <TaskProvider>
      <FormProvider {...methods}>
        <StyledNewTaskDialog
          isOpen={true}
          onClose={onClose}
          onConfirm={onConfirm}
          header={getHeaderTitle}
          footer={getFooter}
          contentWidth
          padContent={false}
          customHeaderIcon={<AddDelayIcon />}
        >
          <NewTaskForm>
            {taskDelayScopes || !isTaskDelayLoading ? getTaskTemplate : <MiniLoader />}
          </NewTaskForm>
        </StyledNewTaskDialog>
      </FormProvider>
    </TaskProvider>
  );
};

DelayDialog.propTypes = {
  task: PropTypes.object,
  methods: PropTypes.object,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  setAnchorEl: PropTypes.func,
  onConfirm: PropTypes.func,
  title: PropTypes.string,
  isCalledfromTaskEscalationColumn: PropTypes.bool,
  mode: PropTypes.string,
  currentDelay: PropTypes.object,
  isTaskTable: PropTypes.bool,
};

DelayDialog.defaultProps = {
  isOpen: false,
  onClose: () => null,
  onConfirm: () => null,
  currentDelay: null,
  methods: null,
  isCalledfromTaskEscalationColumn: null,
  mode: FormMode.CREATE,
  isTaskTable: false,
};
